import React, { useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { get, isEmpty } from 'lodash';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';
import { formatSummaryData } from '@store/aiAlerts/summary/helper';
import { getAlertsSummaryData } from '@src/api/aiAlerts';
import {
  aiAlertsMetricsMetaSelector,
  aiAlertsDimensionMappingSelector,
  intervalSelector,
} from '@store/common/selectors';
import { dimensionsListSelectorKeyValue } from '@store/common/dimensionList/selectors';
import { aiAlertAtom } from '@src/atoms/toggles';
import { CURRENT_PRODUCT_MODE } from '@src/utils/constants';
import { ALERT_TYPE_CONTEXT_MAP } from '@src/utils/aiAlerts/metrics';
import { trackQueryError } from '@utils/sensor/apptracker';
import { DATA_TAGS, useGetData, useSetData } from '../useData';

const defaultValue = {};

const groupByMetric = (alerts) => {
  const metricAlerts = {};
  alerts.forEach((alert) => {
    if (ALERT_TYPE_CONTEXT_MAP[alert.aiAlertType] === CURRENT_PRODUCT_MODE) {
      const metric = alert.pulse5MetricKey;
      if (metric in metricAlerts) {
        metricAlerts[metric].push(alert);
      } else {
        metricAlerts[metric] = [alert];
      }
    }
  });
  return metricAlerts;
};

function useFetchAIAlerts({ enabled = false, dataTag = DATA_TAGS.aiAlertEvents }) {
  const interval = useSelector(intervalSelector);
  const dimensionListObject = useSelector(dimensionsListSelectorKeyValue);
  const dimensionMapping = useSelector(aiAlertsDimensionMappingSelector);
  const alertEnabled = useAtomValue(aiAlertAtom);
  const aiAlertsMetricMeta = useSelector(aiAlertsMetricsMetaSelector);
  const setAiAlertEventData = useSetData({ tag: dataTag, defaultValue });

  // stale time prevents redundant calls from multiple subscribers
  const {
    data: metricAlerts,
    isFetching,
    isError,
  } = useQuery(
    ['ai-alert-events', interval],
    () => {
      return getAlertsSummaryData({ params: { ...interval } }).then((response) => {
        const data = get(response, ['data'], {});
        const { alerts } = formatSummaryData({
          response: data,
          dimensionListObject,
          dimensionMapping,
          aiAlertsMetricMeta,
        });
        const metricAlerts = groupByMetric(alerts);
        return metricAlerts;
      });
    },
    {
      retry: 3,
      refetchOnWindowFocus: false,
      enabled: enabled && alertEnabled && !!interval,
      staleTime: 5 * 60 * 1000, // 5m
      placeholderData: defaultValue,
      onError: (err) => {
        trackQueryError({ type: 'ai-alert-events', err });
      },
    },
  );

  const data = useMemo(() => {
    // to remove the series markers (toggled)
    if (!alertEnabled || isEmpty(metricAlerts)) {
      return defaultValue;
    }
    return metricAlerts;
  }, [alertEnabled, metricAlerts]);

  useEffect(() => {
    setAiAlertEventData(data);
  }, [data, setAiAlertEventData]);

  return { data, isFetching, isError };
}

export const useAiAlertEventData = ({ metric = null, dataTag = DATA_TAGS.aiAlertEvents }) => {
  const data = useGetData({ tag: dataTag, defaultValue });
  if (metric) {
    return data[metric] || [];
  }
  return data;
};

export default useFetchAIAlerts;
